import React, { useEffect, useState } from 'react'
import buttons from '../../../images/icon_button.png'
import joysticks from '../../../images/icon_joystick.png'
import hybrid_hard from '../../../images/icon_hybrid_hard.png'
import touch from '../../../images/icon_touch_hard.png'

import { Box, Slider } from '@mui/material'
import { useIsSmallScreen } from '../../../hooks/useIsSmallScreen'
import { useChangeVolume } from '../../../mqtt/mqttHandlerContext'
import { InstrumentType, SONG_TYPES, Song, Instrument, TouchMode } from '../../../utils/types'
import { Drawer } from '../Welcome'
import Text from '../../../assets/components/Text'
import InstrumentSettings from '../Components/InstrumentSettings'
import { useCurrentUser } from '../../../hooks/contexts/currentUserContext'
import Funkingen from '../../../assets/icons/funkingen_2.png'
import InstrumentInformation from './Components/InstrumentInformation'
import BatteryFullIcon from '@mui/icons-material/BatteryFull'
import Battery3BarIcon from '@mui/icons-material/Battery3Bar'
import BatteryAlertIcon from '@mui/icons-material/BatteryAlert'
import { VolumeIcon } from '../../../components/DynamicVolumeIcon'

import { useContext } from 'react'
import { PolyglotContext } from '../../../polyglot/polyGlotContext'
import { useUpdateConnectedInstrument } from '../../../hooks/contexts/connectedInstrumentsContext'

export const VOLUME_MIN = 0
export const VOLUME_MAX = 0.3
export const VOLUME_STEPS = 7 // 7 positions including min & max
export const VOLUME_STEP = (VOLUME_MAX - VOLUME_MIN) / (VOLUME_STEPS - 1)
export const VOLUME_MARKS = Array.from({ length: VOLUME_STEPS }, (_, i) => ({
    value: parseFloat((VOLUME_MIN + i * VOLUME_STEP).toFixed(3))
}))

// Returnerar korrekt bild beroende på vilket instrument id vi anropar med
function handleImageSource(type: InstrumentType, touchMode?: TouchMode): string {
    switch (type) {
        case 'buttons':
            return buttons
        case 'touch':
            return touch
        case 'joysticks':
            return joysticks
        case 'hybrid':
            return hybrid_hard
        default:
            return buttons
    }
}

function getBatteryPercentage(voltage?: number) {
    if (voltage === undefined || voltage === null || Number.isNaN(voltage)) return null

    const minVoltage = 13.4
    const maxVoltage = 16.4

    const percentage = ((voltage - minVoltage) / (maxVoltage - minVoltage)) * 100
    return Math.max(0, Math.min(100, Math.round(percentage)))
}

export function getBatteryStatus(voltage?: number) {
    const percentage = getBatteryPercentage(voltage)

    if (percentage === null) {
        return {
            percentage: null,
            icon: null
        }
    }

    if (percentage > 66) {
        return {
            percentage,
            icon: <BatteryFullIcon fontSize="small" color="success" />
        }
    }

    if (percentage > 25) {
        return {
            percentage,
            icon: <Battery3BarIcon fontSize="small" color="warning" />
        }
    }

    return {
        percentage,
        icon: <BatteryAlertIcon fontSize="small" color="error" />
    }
}

interface InstrumentsProps {
    currentSong: Song | undefined
    instrumentVolumes: { [key: string]: number }
    setInstrumentVolumes: React.Dispatch<React.SetStateAction<{ [key: string]: number }>>
    connectedInstruments: Instrument[]
}

const Instruments = ({ currentSong, instrumentVolumes, setInstrumentVolumes, connectedInstruments }: InstrumentsProps) => {
    const { polyglot } = useContext(PolyglotContext)
    const updateConnectedInstruments = useUpdateConnectedInstrument()

    const [isOpen, setIsOpen] = useState(false)
    const [selectedInstrument, setSelectedInstrument] = useState<Instrument>({
        deviceId: '',
        type: '',
        serialNr: '',
        batteryLevel: 0,
        volume: 0,
        arrangement: ''
    })

    // const postMqttChangeVolume = usePostMqttChangeVolume()
    const changeVolume = useChangeVolume()
    const isSmallScreen = useIsSmallScreen()

    const currentUser = useCurrentUser()
    const songList = currentUser?.songList
    const shouldShowSettings = currentSong?.type === SONG_TYPES.KEY

    const toggleDrawer = ({ instrumentId, instrumentType, instrumentSerialNr, color }: { instrumentId?: string; instrumentType?: InstrumentType; instrumentSerialNr?: string; color?: string }) => {
        setIsOpen(!isOpen)
        setSelectedInstrument({
            deviceId: instrumentId ?? '',
            type: instrumentType ?? '',
            serialNr: instrumentSerialNr ?? '',
            batteryLevel: 0,
            volume: 0,
            arrangement: ''
        })
    }

    // send only when the user releases the thumb (mouse up / touch end)
    const handleSliderCommit = (_: Event | React.SyntheticEvent, value: number | number[], instrument: Instrument) => {
        const vol = Array.isArray(value) ? value[0] : value
        if (instrument.protocol === '1.0') {
            setInstrumentVolumes((prev) => ({ ...prev, [instrument.deviceId]: vol }))
            changeVolume({ volume: parseFloat(vol.toFixed(3)), deviceId: instrument.deviceId })
        } else {
            updateConnectedInstruments({
                ...instrument,
                volume: vol
            })
            changeVolume({ volume: parseFloat(vol.toFixed(3)), deviceId: instrument.deviceId })
        }
    }

    // update UI state while dragging (no MQTT)
    const handleSliderChange = (_: Event | React.SyntheticEvent, value: number | number[], instrument: Instrument) => {
        const vol = Array.isArray(value) ? value[0] : value
        if (instrument.protocol === '1.0') {
            setInstrumentVolumes((prev) => ({ ...prev, [instrument.deviceId]: vol }))
        } else {
            updateConnectedInstruments({
                ...instrument,
                volume: vol
            })
        }
    }

    // Fetch initial volume values from storage when the component mounts
    useEffect(() => {
        const storedVolumesString = localStorage.getItem('instrumentVolumes')
        if (storedVolumesString) {
            const storedVolumes = JSON.parse(storedVolumesString)
            setInstrumentVolumes(storedVolumes)
        }
    }, [])

    // Save volume values to storage whenever they change
    useEffect(() => {
        localStorage.setItem('instrumentVolumes', JSON.stringify(instrumentVolumes))
    }, [instrumentVolumes])

    return connectedInstruments && connectedInstruments.length > 0 ? (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
                height: isSmallScreen ? '80%' : '100%',
                gridAutoRows: isSmallScreen ? '10rem' : 'auto'
            }}
        >
            {connectedInstruments.map((instrument: Instrument, idx: number) => (
                <Box key={idx} sx={{ display: 'flex', height: isSmallScreen ? '60%' : '150px' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}
                    >
                        <VolumeIcon value={instrumentVolumes[instrument.deviceId]} />
                        <Slider
                            orientation="vertical"
                            aria-label="Volume"
                            value={instrument.protocol !== '1.0' ? instrument.volume : (instrumentVolumes[instrument.deviceId] ?? VOLUME_MARKS[1].value)}
                            min={VOLUME_MIN}
                            max={VOLUME_MAX}
                            step={VOLUME_STEP}
                            marks={VOLUME_MARKS} // show the 5 ticks
                            valueLabelDisplay="off"
                            onChange={(e, v) => handleSliderChange(e, v, instrument)}
                            onChangeCommitted={(e, v) => handleSliderCommit(e, v, instrument)}
                            sx={{
                                height: 100,
                                width: 10,
                                // thumb
                                '& .MuiSlider-thumb': {
                                    width: 14,
                                    height: 14,
                                    bgcolor: '#d5e6c3',
                                    border: '1px solid #3fad4471',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.25)'
                                },
                                // rail (full length) gradient
                                '& .MuiSlider-rail': {
                                    opacity: 1,
                                    background: 'linear-gradient(to top, #d5e6c3 0%, #fee57b 80%, #e85e46 100%)',
                                    // "linear-gradient(to top, #e85e46 0%, #fee57b 30%, #d5e6c3 100%)",
                                    width: 6,
                                    borderRadius: 1
                                },
                                // track (filled portion) same gradient so it blends nicely
                                '& .MuiSlider-track': {
                                    background: 'linear-gradient(to top, #d5e6c3 0%, #fee57b 80%, #e85e46 100%)',
                                    width: 6,
                                    borderRadius: 1,
                                    borderColor: '#d5e6c3'
                                },
                                // // marks (ticks)
                                '& .MuiSlider-mark': {
                                    width: 6,
                                    height: 3,
                                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                                    borderRadius: 1,
                                    left: '50%',
                                    transform: 'translateX(-50%)'
                                }
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            position: 'relative',
                            display: 'inline-block' // important so it wraps the image nicely
                        }}
                    >
                        <img src={handleImageSource(instrument.type, instrument.touchMode)} alt="Bild på instrument" height={isSmallScreen ? '100%' : '130px'} style={{ display: 'block' }} />

                        {instrument.protocol !== '1.0' && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: isSmallScreen ? 10 : 35,
                                    left: isSmallScreen ? 5 : 10,
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: '4px',
                                    py: '2px',
                                    borderRadius: '4px'
                                }}
                            >
                                {getBatteryStatus(instrument.batteryLevel).icon}
                                <Text size={'tiny'} italic bold>
                                    {getBatteryStatus(instrument.batteryLevel).percentage !== null ? `${getBatteryStatus(instrument.batteryLevel).percentage}%` : '-'}
                                </Text>
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                        }}
                    >
                        {songList && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    height: isSmallScreen ? '100%' : 'auto'
                                }}
                            >
                                <InstrumentInformation instrument={instrument} songList={songList} shouldShowSettings={shouldShowSettings} isSmallScreen={isSmallScreen} currentSong={currentSong} />
                            </Box>
                        )}

                        <Drawer variant="bottom" isOpen={isOpen} toggleDrawer={() => toggleDrawer({})} heading={polyglot.t('home.instrumentInformation.headingChooseSound')}>
                            <InstrumentSettings currentSong={currentSong} selectedInstrument={selectedInstrument} />
                        </Drawer>
                    </Box>
                </Box>
            ))}
        </Box>
    ) : (
        <Box
            sx={{
                height: '20%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: isSmallScreen ? 'left' : 'center',
                paddingRight: isSmallScreen ? 0 : '10em'
            }}
        >
            <img src={Funkingen} alt="Funkingen" style={{ height: '100%' }} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Text italic size={'heading'} textAlign="center">
                    {polyglot.t('home.instruments.headingWaiting')}
                </Text>
            </Box>
        </Box>
    )
}

export default Instruments
